import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, User, Bot } from 'lucide-react';
import { buildSummaryTree, summarizeSpanKind } from '../../utils/traceSummaryTree';
import { formatDuration, formatCost } from '../../utils/formatters';
import { truncateText } from '../../utils/traceIoParser';
import { palette, withOpacity } from '../../utils/designTokens';
import { getDetectionLabel } from '../../utils/signalUtils';
import { getDetectionsForSpan } from '../../utils/spanDetections';
import ServiceChips from './components/shared/ServiceChips';

const kindBadgeStyles = {
  llm: 'text-accent-warm-base bg-accent-warm-base/10',
  tool: 'text-accent-secondary-base bg-accent-secondary-base/10',
  chain: 'text-accent-primary-base bg-accent-primary-base/10',
  agent: 'text-accent-primary-base bg-accent-primary-base/10',
  retriever: 'text-accent-primary-base bg-accent-primary-base/10',
  reranker: 'text-accent-secondary-base bg-accent-secondary-base/10',
  embedding: 'text-accent-secondary-base bg-accent-secondary-base/10',
  unspecified: 'text-text-muted bg-background-tertiary',
};

const parseAttributes = (attributes) => {
  if (!attributes) return null;
  if (typeof attributes === 'string') {
    try { return JSON.parse(attributes); } catch { return null; }
  }
  return attributes;
};

const getToolName = (span) => {
  const attrs = parseAttributes(span.attributes);
  if (!attrs) return null;
  return attrs?.tool?.name || attrs?.['tool.name'] || attrs?.tool_name || attrs?.['tool_name'] || null;
};

const SignalBadge = ({ label, variant = 'neutral' }) => {
  const variants = {
    danger: 'text-danger bg-danger/10',
    warning: 'text-warning bg-warning/10',
    info: 'text-accent-primary-base bg-accent-primary-base/10',
    success: 'text-green-400 bg-green-500/10',
    neutral: 'text-text-muted bg-background-tertiary',
    warm: 'text-accent-warm-base bg-accent-warm-base/10',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border border-border-strong ${variants[variant] || variants.neutral}`}>
      {label}
    </span>
  );
};

const getSpanSignals = (span, traceDetections = []) => {
  const signals = [];
  if (!span) return signals;

  const displayedDetections = getDetectionsForSpan(span, traceDetections);

  const signalVariantFromPolarity = (polarity) => {
    if (polarity === 'negative') return 'danger';
    if (polarity === 'positive') return 'success';
    return 'neutral';
  };

  if (displayedDetections.length > 0) {
    const counts = new Map();

    displayedDetections.forEach((detection) => {
      const label = getDetectionLabel(detection);
      if (!label) return;

      const variant = signalVariantFromPolarity(detection?.polarity);
      const key = `${label.toLowerCase()}|${variant}`;
      if (!counts.has(key)) {
        counts.set(key, { label, variant, count: 0 });
      }
      counts.get(key).count += 1;
    });

    [...counts.values()].forEach(({ label, variant, count }) => {
      signals.push({
        label: count > 1 ? `${label} (${count})` : label,
        variant,
      });
    });
  }

  const customEvals = span.metrics?.custom_evals || span.metrics?.custom_evaluations || [];
  const spanEvals = Array.isArray(customEvals)
    ? customEvals.filter(e => !e.span_id || e.span_id === span.span_id)
    : [];
  if (spanEvals.length > 0) {
    const failed = spanEvals.filter(e => e.passed === false || e.error).length;
    signals.push({
      label: `Eval ${failed > 0 ? `${failed} failed` : spanEvals.length}`,
      variant: failed > 0 ? 'warning' : 'info'
    });
  }

  const toolJsonErrors = span.metrics?.tool_json_errors || [];
  const toolSchemaErrors = span.metrics?.tool_schema_errors || [];
  const toolIssueCount = (Array.isArray(toolJsonErrors) ? toolJsonErrors.length : 0) +
    (Array.isArray(toolSchemaErrors) ? toolSchemaErrors.length : 0);
  if (toolIssueCount > 0) {
    signals.push({ label: `Tool Issues ${toolIssueCount}`, variant: 'warning' });
  }

  if (span.status_code === 'ERROR' || span.metrics?.has_error) {
    signals.push({ label: 'Error', variant: 'danger' });
  }

  return signals;
};

const treeHasSpan = (nodes = [], spanId) => {
  if (!spanId) return false;
  return nodes.some((node) => (
    node?.span?.span_id === spanId || treeHasSpan(node.children || [], spanId)
  ));
};

const SpanTreeNode = ({ node, depth = 0, selectedSpanId, onSpanSelect, primaryModel, traceDetections = [], isMultiService = false, isMultiEnv = false, evidenceSpanIds = new Set() }) => {
  const [expanded, setExpanded] = useState(true);
  const rowRef = useRef(null);
  const hasChildren = node.children && node.children.length > 0;
  const kind = node.kind || summarizeSpanKind(node.span.span_kind);
  const badgeStyle = kindBadgeStyles[kind] || kindBadgeStyles.unspecified;
  const duration = formatDuration(node.span.duration_ms);
  const toolName = getToolName(node.span);
  const modelName = node.span.llm_model_name;
  const showModel = modelName && modelName !== primaryModel;
  const tokens = node.span.llm_token_count_total || node.span.token_count || null;
  const cost = node.span.cost ?? node.span.llm_cost ?? null;
  const spanName = node.span.span_name || node.span.name || 'Span';
  const isSelected = selectedSpanId === node.span.span_id;
  const isEvidence = evidenceSpanIds?.has?.(node.span.span_id);
  const signals = useMemo(
    () => getSpanSignals(node.span, traceDetections),
    [node.span, traceDetections]
  );

  useEffect(() => {
    if (!isSelected || !rowRef.current) return;
    rowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [isSelected, selectedSpanId]);

  return (
    <div>
      <div
        ref={rowRef}
        className={`flex items-start gap-2 py-2 px-2 rounded-lg border transition-colors cursor-pointer ${isSelected ? 'border-accent-primary-base border-l-[3px]' : isEvidence ? 'border-warning/70 border-l-[3px]' : 'border-transparent hover:bg-background-tertiary'}`}
        style={{ paddingLeft: `${depth * 16 + 4}px`, ...(isSelected ? { backgroundColor: palette.selection.bg } : isEvidence ? { backgroundColor: withOpacity(palette.status.warning, 8) } : {}) }}
        onClick={() => onSpanSelect(node.span)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="mt-0.5 p-0.5 rounded hover:bg-background-quaternary"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="w-5" />
        )}

        <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${badgeStyle}`}>
          {kind.toUpperCase()}
        </span>

        <div className="flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-sm text-text-primary font-medium">{spanName}</span>
              {isEvidence && (
                <SignalBadge label="Classifier Evidence" variant="warning" />
              )}
            </div>
            {isMultiService && node.span.service_name && (
              <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] border" style={{ color: 'var(--color-text-muted)', borderColor: palette.borders.subtle }} title={isMultiEnv && node.span.environment ? `${node.span.service_name} · ${node.span.environment}` : node.span.service_name}>
                {isMultiEnv && node.span.environment ? `${node.span.service_name} · ${node.span.environment}` : node.span.service_name}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-text-muted">
            {toolName && <span>Tool: {toolName}</span>}
            {duration !== '—' && <span>Latency: {duration}</span>}
            {tokens != null && <span>Tokens: {tokens.toLocaleString()}</span>}
            {cost != null && <span>Cost: {formatCost(cost)}</span>}
            {node.span.status && <span>Status: {node.span.status}</span>}
            {showModel && <span>Model: {modelName}</span>}
          </div>
          {signals.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {signals.map((signal, idx) => (
                <SignalBadge key={`${signal.label}-${idx}`} label={signal.label} variant={signal.variant} />
              ))}
            </div>
          )}
        </div>
      </div>

      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <SpanTreeNode
              key={child.span.span_id}
              node={child}
              depth={depth + 1}
              selectedSpanId={selectedSpanId}
              onSpanSelect={onSpanSelect}
              primaryModel={primaryModel}
              traceDetections={traceDetections}
              isMultiService={isMultiService}
              isMultiEnv={isMultiEnv}
              evidenceSpanIds={evidenceSpanIds}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ChatBubble = ({ type, text }) => {
  if (!text) return null;
  const isUser = type === 'user';
  return (
    <div className={`px-3 py-1.5 flex ${isUser ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[85%] overflow-hidden rounded-2xl ${isUser ? 'rounded-tl-sm border border-border-strong bg-background-quaternary' : 'rounded-tr-sm border border-accent-primary-base/60 bg-accent-primary-base shadow-sm'} px-3 py-2`}>
        <div className={`flex items-start gap-1.5 ${isUser ? '' : 'justify-end'}`}>
          {isUser
            ? <User size={14} className="shrink-0 mt-0.5" style={{ color: palette.accents.primary.base }} />
            : <Bot size={14} className="shrink-0 mt-0.5" style={{ color: palette.text.inverse }} />
          }
          <div className={`text-sm whitespace-pre-wrap break-words text-left min-w-0 ${isUser ? 'text-text-primary' : 'text-text-inverse'}`} style={{ overflowWrap: 'anywhere' }}>
            {truncateText(text, 480)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TurnTab({ spans, selectedSpan, onSpanSelect, trace, traceDetections = [], filterMode = 'summary', onFilterModeChange, isMultiService = false, isMultiEnv = false, evidenceSpanIds = new Set(), participants = [] }) {
  const mergedTraceDetections = useMemo(() => {
    if (Array.isArray(traceDetections) && traceDetections.length > 0) return traceDetections;
    if (Array.isArray(trace?.detections) && trace.detections.length > 0) return trace.detections;
    return [];
  }, [traceDetections, trace]);

  const spanTree = useMemo(
    () => buildSummaryTree(spans || [], { includeAllNodes: filterMode === 'all' }),
    [spans, filterMode]
  );

  // Read the server-computed column value directly (first_user_input / last_assistant_output);
  // no client-side re-extraction. `input`/`output` are normalized to {text} objects (or strings).
  const inputText = useMemo(() => {
    const v = trace?.input;
    return (typeof v === 'string' ? v : v?.text) ?? null;
  }, [trace]);
  const outputText = useMemo(() => {
    const v = trace?.output;
    return (typeof v === 'string' ? v : v?.text) ?? null;
  }, [trace]);
  const primaryModel = useMemo(() => {
    const models = Array.isArray(trace?.models) ? trace.models : [];
    return models.length === 1 ? models[0] : null;
  }, [trace]);

  // Auto-select first span if none selected
  useEffect(() => {
    if (!selectedSpan && spanTree.length > 0) {
      onSpanSelect(spanTree[0].span);
    }
  }, [spanTree, selectedSpan, onSpanSelect]);

  useEffect(() => {
    if (!selectedSpan?.span_id || filterMode !== 'summary') return;
    if (!treeHasSpan(spanTree, selectedSpan.span_id)) {
      onFilterModeChange('all');
    }
  }, [filterMode, onFilterModeChange, selectedSpan?.span_id, spanTree]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-muted">
            {filterMode === 'summary' ? 'Showing key spans' : 'Showing all spans'}
          </span>
          <ServiceChips participants={participants} />
        </div>
        <button
          type="button"
          className="text-xs px-2 py-1 rounded border border-border-subtle bg-background-tertiary hover:bg-background-quaternary text-text-primary"
          onClick={() => onFilterModeChange(filterMode === 'summary' ? 'all' : 'summary')}
        >
          {filterMode === 'summary' ? 'Show All' : 'Show Summary'}
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        <ChatBubble type="user" text={inputText} />
        <div className="p-2 space-y-1">
          {spanTree.length === 0 ? (
            <div className="text-xs text-text-muted p-2">No spans available</div>
          ) : (
            spanTree.map((node) => (
              <SpanTreeNode
                key={node.span.span_id}
                node={node}
                selectedSpanId={selectedSpan?.span_id}
                onSpanSelect={onSpanSelect}
                primaryModel={primaryModel}
                traceDetections={mergedTraceDetections}
                isMultiService={isMultiService}
                isMultiEnv={isMultiEnv}
                evidenceSpanIds={evidenceSpanIds}
              />
            ))
          )}
        </div>
        <ChatBubble type="bot" text={outputText} />
      </div>
    </div>
  );
}
